import { createFileRoute, Outlet } from "@tanstack/react-router";
import { loadOnboardingStatusForContext } from "@/server/route-guard";
import { executeStateMachineRedirect } from "@/lib/routing/onboarding-redirect";
import { sanitizeAuthReturnTo } from "@/lib/routing/auth-return";

const ONBOARDING_PAGE_PATHS = new Set([
  "/onboarding",
  "/onboarding/activation",
  "/onboarding/profile",
  "/onboarding/waitlist",
  "/onboarding/waitlist/pending",
]);

/**
 * Onboarding layout route
 *
 * This route uses the optimized pattern:
 * - beforeLoad: Fast authentication check only (~10ms)
 * - loader: All data fetching and state machine redirects
 *
 * This ensures:
 * - Child routes can access loader data
 * - Loader cache is fully controlled by staleTime
 * - No unnecessary API calls during onboarding navigation
 */
export const Route = createFileRoute("/onboarding")({
  validateSearch: (search: Record<string, unknown>): { return_to?: string } => ({
    return_to:
      typeof search.return_to === "string" ? sanitizeAuthReturnTo(search.return_to) : undefined,
  }),
  component: OnboardingLayout,

  // Cache onboarding status for 5 minutes (aligns with workspace route)
  staleTime: 5 * 60_000,
  gcTime: 10 * 60_000,

  // 临时移除 beforeLoad 的服务端鉴权，避免登录后 Cookie 尚未稳定导致跳回登录

  // loader: All data fetching happens here
  // Benefits of this approach:
  // - Loader cache is fully controlled by staleTime
  // - Onboarding navigation uses cached data without API calls
  // - True stale-while-revalidate behavior
  loader: async ({ location }) => {
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    if (!ONBOARDING_PAGE_PATHS.has(pathname)) {
      return {};
    }

    const { onboardingStatus } = await loadOnboardingStatusForContext();
    const search = location.search as { return_to?: unknown };
    const returnTo =
      typeof search.return_to === "string" ? sanitizeAuthReturnTo(search.return_to) : undefined;

    executeStateMachineRedirect(onboardingStatus, pathname, "onboarding.loader", {
      return_to: returnTo,
    });

    return { onboardingStatus };
  },
});

function OnboardingLayout() {
  return <Outlet />;
}
